import React, { memo, useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import NewsletterSignup from './NewsletterSignup';

const STORAGE_KEY = 'dwb_exit_popup_dismissed';
const COOLDOWN_DAYS = 7;

/**
 * Exit-intent popup with special offer and newsletter signup.
 * Shows when user moves mouse toward top of viewport.
 * Respects 7-day cooldown after dismissal.
 */
const ExitIntentPopup = memo(function ExitIntentPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);

  // Check if popup should show based on cooldown
  const shouldShowPopup = useCallback(() => {
    try {
      const dismissedAt = localStorage.getItem(STORAGE_KEY);
      if (!dismissedAt) return true;
      
      const dismissedDate = new Date(parseInt(dismissedAt, 10));
      const now = new Date();
      const daysDiff = (now.getTime() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      
      return daysDiff >= COOLDOWN_DAYS;
    } catch {
      return true;
    }
  }, []);

  // Handle dismiss with cooldown
  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    try {
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    } catch {
      // localStorage may be unavailable
    }
  }, []);

  // Handle successful signup
  const handleSuccess = useCallback(() => {
    // Keep popup visible briefly to show success message
    setTimeout(() => {
      handleDismiss();
    }, 2000);
  }, [handleDismiss]);

  // Exit intent detection
  useEffect(() => {
    if (hasTriggered || !shouldShowPopup()) return;

    // Desktop: detect mouse leaving viewport
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !hasTriggered) {
        setHasTriggered(true);
        setIsVisible(true);
      }
    };

    // Mobile: detect rapid scroll up (intent to leave)
    let lastScrollY = window.scrollY;
    let scrollVelocity = 0;
    
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      scrollVelocity = lastScrollY - currentScrollY;
      
      // If scrolling up fast and near top of page
      if (scrollVelocity > 50 && currentScrollY < 200 && !hasTriggered) {
        setHasTriggered(true);
        setIsVisible(true);
      }
      
      lastScrollY = currentScrollY;
    };

    // Add listeners after a delay (don't trigger immediately on page load)
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
      window.addEventListener('scroll', handleScroll, { passive: true });
    }, 5000); // Wait 5 seconds before enabling exit intent

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasTriggered, shouldShowPopup]);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isVisible) {
        handleDismiss();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, handleDismiss]);

  // Prevent body scroll when popup is open
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isVisible]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={handleDismiss}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          
          {/* Popup Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-background rounded-2xl shadow-2xl max-w-md w-full mx-auto overflow-hidden"
          >
            {/* Decorative gradient header */}
            <div className="h-2 bg-gradient-to-r from-primary via-blue-500 to-purple-500" />
            
            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close popup"
            >
              <X className="w-4 h-4" />
            </button>
            
            {/* Content */}
            <div className="p-6 pt-8">
              {/* Special offer badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-500/10 text-green-600 dark:text-green-400 rounded-full text-sm font-bold mb-4">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                🎁 Exclusive 15% OFF
              </div>
              
              <h2 className="text-2xl font-bold mb-2">Wait! Here's 15% Off 🎉</h2>
              <p className="text-muted-foreground mb-6">
                Before you leave, grab your <strong>exclusive 15% discount</strong> on any website project! Whether it's a brand new site or a redesign — this deal won't last forever.
              </p>
              
              <NewsletterSignup variant="popup" onSuccess={handleSuccess} />
              
              {/* Skip link */}
              <button
                onClick={handleDismiss}
                className="w-full mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                No thanks, I'll pay full price
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default ExitIntentPopup;
